import { Box, BoxProps, Button, Code, useToast } from "@chakra-ui/react";
import { CopyIcon } from "@chakra-ui/icons";
import { ReactNode } from "react";

type CodeBlockProps = {
  children: ReactNode;
  variant?: "light" | "dark";
  containerProps?: BoxProps;
  copyText?: string;
};

export default function CodeBlock({
  children,
  variant = "light",
  containerProps,
  copyText,
}: CodeBlockProps) {
  const toast = useToast();
  const styles =
    variant === "dark"
      ? {
          bg: "black",
          color: "white",
          borderRadius: "md",
          boxShadow: undefined as any,
          p: 4,
        }
      : {
          bg: "#edf2f7",
          color: "black",
          borderRadius: 30,
          boxShadow: "lg",
          p: 8,
        };

  const resolvedCopyText =
    copyText ?? (typeof children === "string" ? children : undefined);

  const canCopy = variant === "dark" && !!resolvedCopyText;

  const handleCopy = async () => {
    if (!resolvedCopyText) return;

    try {
      await navigator.clipboard.writeText(resolvedCopyText);
      toast({
        title: "Success!",
        description: "Code successfully copied to your clipboard",
        status: "success",
        duration: 4000,
        isClosable: true,
      });
    } catch {
      toast({
        title: "Error!",
        description: "Unable to copy to clipboard",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
  };

  return (
    <Box
      as="pre"
      overflowX="auto"
      bg={styles.bg}
      color={styles.color}
      borderRadius={styles.borderRadius}
      boxShadow={styles.boxShadow}
      position={variant === "dark" ? "relative" : undefined}
      p={styles.p}
      pt={variant === "dark" && canCopy ? 12 : undefined}
      pr={variant === "dark" && canCopy ? 12 : undefined}
      mb={10}
      {...containerProps}
    >
      {canCopy ? (
        <Button
          color={"white"}
          _hover={{
            bg: "white",
            color: "black",
            borderColor: "black",
          }}
          size="sm"
          position="absolute"
          top="0"
          right="0"
          mt={"15px"}
          mr={"10px"}
          onClick={handleCopy}
          variant={"outline"}
          leftIcon={<CopyIcon />}
        >
          Copy
        </Button>
      ) : null}
      <Code bg="transparent" color="inherit" whiteSpace="pre-wrap">
        {children}
      </Code>
    </Box>
  );
}

